<!--
  This component displays a menu to select a number from a range and a dialog to
  enter the value using a text input.
-->
<template>
  <v-menu
    v-model="show"
    offset-y
    min-width="140"
    max-width="140"
    :light="light"
    :dark="dark"
    :disabled="disabled"
    :close-on-content-click="false"
  >
    <template #activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs" />
    </template>
    <v-system-bar
      height="32"
      :color="color"
      class="pr-1"
    >
      <span v-text="title" />
      <v-spacer />
      <v-icon
        v-if="value === 0"
        color="primary"
        small
        v-text="icons.close"
        @click="show = false"
      />
      <template v-else>
        <v-icon small v-text="icons.minusCircle" @click="decrement()" />
        <v-icon small v-text="icons.plusCircle" @click="increment()" />
      </template>
    </v-system-bar>
    <v-list
      dense
      subheader
      :color="color"
    >
      <v-divider />
      <v-list-item-group
        v-model="selected"
        color="primary"
        :mandatory="mandatory"
      >
        <v-list-item
          v-for="(n, i) in parseInt(display)"
          :key="n"
          @click="select(min + i)"
        >
          <v-list-item-title class="text-center" v-text="min + i" />
        </v-list-item>
        <v-list-item @click="editCustom()">
          <v-list-item-title class="text-center">
            <span v-if="showCustomValue">
              <v-icon small v-text="icons.left" />
              {{ value }}
              <v-icon small v-text="icons.action" />
            </span>
            <v-icon
              v-else
              v-text="icons.moreHorizontal"
            />
          </v-list-item-title>
        </v-list-item>
      </v-list-item-group>
    </v-list>
    <h-quantity
      v-model="customDialog"
      :min="min"
      :max="max"
      :initial="value"
      @submit="setCustom"
      @cancel="init()"
    />
  </v-menu>
</template>

<script>
  import Lockable from '@/Mixins/Lockable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import HQuantity from '@/Components/Quantity'

  export default {
    mixins: [
      Lockable,
      UsesIcons,
    ],

    components: {
      HQuantity,
    },

    props: {
      value: {
        required: false,
        type: Number,
        default: 0,
      },
      title: {
        required: false,
        type: String,
        default: '',
      },
      display: {
        required: false,
        type: [Number, String],
        default: 8,
      },
      color: {
        required: false,
        type: String,
        default: 'secondary',
      },
      light: {
        required: false,
        type: Boolean,
        default: false,
      },
      dark: {
        required: false,
        type: Boolean,
        default: false,
      },
      disabled: {
        required: false,
        type: Boolean,
        default: false,
      },
      min: {
        required: false,
        type: Number,
        default: 1,
      },
      max: {
        required: false,
        type: Number,
        default: 10000, // like in XEF's GSNumberInputViewController
      },
    },

    data: () => ({
      show: undefined,
      selected: null,
      mandatory: false,
      customDialog: false,
    }),

    computed: {
      inRange () {
        return this.value >= this.min && this.value <= this.last
      },
      last () {
        return this.min + parseInt(this.display) - 1
      },
      showCustomValue () {
        return this.value !== 0 && ! this.inRange
      },
    },

    created () {
      this.init()
    },

    watch: {
      value () {
        this.init()
      },
    },

    methods: {
      init () {
        if (this.value === 0) {
          this.selected = null
          this.mandatory = false
        } else if (this.inRange) {
          this.selected = this.value - this.min
          this.mandatory = true
        } else {
          this.selected = parseInt(this.display)
          this.mandatory = true
        }
      },
      select (newValue) {
        if (this.locked || newValue === this.value) {
          return
        }
        this.setValue(newValue)
        this.show = false
      },
      increment () {
        if (this.locked || this.value >= this.max) {
          return
        }
        this.setValue(this.value + 1)
      },
      decrement () {
        if (this.locked || this.value <= this.min) {
          return
        }
        this.setValue(this.value - 1)
      },
      editCustom () {
        if (this.locked) {
          return
        }
        this.lock()
        this.customDialog = true
      },
      setCustom (v) {
        if (this.locked || ! v) {
          return
        }
        this.setValue(v)
        this.show = false
      },
      setValue (v) {
        this.lock()
        this.$emit('input', Math.min(v, this.max))
        setTimeout(() => this.init(), 100)
      },
    },
  }
</script>
